import { ReactNode, RefObject, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronDown,
  faChevronUp,
  faTrash,
} from '@fortawesome/pro-regular-svg-icons';
import { CollapsibleSection } from './CollapsibleSection';
import { ToggleRow } from './ToggleRow';
import * as styles from './RepeatingFieldShell.css';

type RepeatingFieldShellProps = {
  title: string;
  subtitle?: ReactNode;
  itemLabel: string;
  fields: { id: string }[];
  enabled: boolean;
  onToggleEnabled: (next: boolean) => void;
  onRemove: (index: number) => void;
  renderItem: (index: number) => ReactNode;
  footer: ReactNode;
  isOpen: boolean;
  onOpenChange: (next: boolean) => void;
  isSubmitting?: boolean;
  testId?: string;
  errorMessage?: string;
  errorAnchorRef?: RefObject<HTMLDivElement>;
};

const ItemCard = ({
  index,
  itemLabel,
  onRemove,
  children,
  isSubmitting,
  testId,
}: {
  index: number;
  itemLabel: string;
  onRemove: () => void;
  children: ReactNode;
  isSubmitting?: boolean;
  testId?: string;
}) => {
  const [isOpen, setIsOpen] = useState(true);
  return (
    <div className={styles.itemCard} data-testid={testId}>
      <div className={styles.itemHeader}>
        <div className={styles.itemHeaderLeft}>
          <span>#{index + 1}</span>
          <span>{itemLabel}</span>
        </div>
        <div className={styles.itemHeaderRight}>
          <button
            type="button"
            className={styles.headerIconButton}
            onClick={onRemove}
            disabled={isSubmitting}
            aria-label={`Remove ${itemLabel} ${index + 1}`}
          >
            <FontAwesomeIcon icon={faTrash} />
          </button>
          <button
            type="button"
            className={styles.headerIconButton}
            onClick={() => setIsOpen((v) => !v)}
            aria-expanded={isOpen}
            aria-label={`${isOpen ? 'Collapse' : 'Expand'} ${itemLabel} ${
              index + 1
            }`}
          >
            <FontAwesomeIcon icon={isOpen ? faChevronUp : faChevronDown} />
          </button>
        </div>
      </div>
      <div className={styles.itemBody} hidden={!isOpen}>
        {children}
      </div>
    </div>
  );
};

export const RepeatingFieldShell = ({
  title,
  subtitle,
  itemLabel,
  fields,
  enabled,
  onToggleEnabled,
  onRemove,
  renderItem,
  footer,
  isOpen,
  onOpenChange,
  isSubmitting,
  testId,
  errorMessage,
  errorAnchorRef,
}: RepeatingFieldShellProps) => (
  <CollapsibleSection title={title} isOpen={isOpen} onOpenChange={onOpenChange}>
    {subtitle ? <div className={styles.subtitle}>{subtitle}</div> : null}
    {errorMessage ? (
      <div className={styles.errorMessage} role="alert" ref={errorAnchorRef}>
        {errorMessage}
      </div>
    ) : null}
    <ToggleRow
      label="Turn on"
      checked={enabled}
      onChange={onToggleEnabled}
      ariaLabel={`Turn ${title} section on`}
      disabled={isSubmitting}
      testId={testId ? `${testId}-toggle` : undefined}
    />
    {enabled
      ? fields.map((field, index) => (
          <ItemCard
            key={field.id}
            index={index}
            itemLabel={itemLabel}
            onRemove={() => onRemove(index)}
            isSubmitting={isSubmitting}
            testId={testId ? `${testId}-item-${index}` : undefined}
          >
            {renderItem(index)}
          </ItemCard>
        ))
      : null}
    {enabled ? <div className={styles.footer}>{footer}</div> : null}
  </CollapsibleSection>
);
